/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { Accordion } from '@app/components/Atoms/Accordion'
import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import {
  ScenariosByAsset,
  useScenariosFetcher,
} from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/scenariosFetcher'
import { useFetchHazardTechnicalReports } from '@app/components/Property/hooks/useFetchHazardTechnicalReports'
import { HazardTechnicalReport } from '@app/components/Property/types'
import { areScenariosExposed } from '@app/components/Property/utils/areScenariosExposed'
import { isPropertyExposedLegend } from '@app/components/Property/utils/isPropertyExposedLegend'
import {
  propertyAssetTypeAtom,
  propertyConfigAtom,
  reportHazardsAtom,
  selectedAssetIdAtom,
  selectedAssetInfoAtom,
} from '@app/state/propertyAtoms'
import { useAtomValue } from 'jotai'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { SlideInFooter } from '../SlideInFooter'
import { TechnicalReportPopup } from '../TechnicalReportPopup'
import { HazardAccordion } from './HazardAccordion'
import { footerLegend, noDetailsStyle, scrollContainer, tabParent } from './HazardsTab.styles'
import { canExportAtom } from '@app/hooks/useUserProfile'

export const HazardsTab = () => {
  const theme = useTheme()
  const { id } = useParams<{ id: string }>()

  const { data: propertyConfiguration, isLoading: propertyConfigLoading } =
    useAtomValue(propertyConfigAtom)
  const canExport = useAtomValue(canExportAtom)

  const propertyAssetType = useAtomValue(propertyAssetTypeAtom)
  const selectedAssetId = useAtomValue(selectedAssetIdAtom)
  const selectedAssetInfoQuery = useAtomValue(selectedAssetInfoAtom)
  const selectedAssetInfo = selectedAssetInfoQuery.data
  const selectedAssetInfoIsLoading = selectedAssetInfoQuery.isLoading
  const reportHazards = useAtomValue(reportHazardsAtom)
  const { data: technicalReports } = useFetchHazardTechnicalReports(reportHazards)

  const [knownRisksLoading, setKnownRisksLoading] = useState(true)
  const [popupReport, setPopupReport] = useState<HazardTechnicalReport | null>(null)
  // Can't use layersData till hazards dialog data is available
  const layersData = useLayerDataFetcher()
  const hazardsData = useMemo(() => {
    return layersData?.hazardDialogData?.hazardDetails || []
  }, [layersData?.hazardDialogData?.hazardDetails])
  const scenarios = useScenariosFetcher(
    propertyAssetType || propertyConfiguration?.asset_type,
    selectedAssetId || id,
    setKnownRisksLoading,
  )
  const legalDescription = useMemo(() => {
    if (
      selectedAssetInfo &&
      typeof selectedAssetInfo === 'object' &&
      !Array.isArray(selectedAssetInfo) &&
      'LegalDescription' in selectedAssetInfo
    ) {
      return (selectedAssetInfo as { LegalDescription?: string | number }).LegalDescription
    }
    return undefined
  }, [selectedAssetInfo])
  const scenariosByUid = useMemo(() => {
    if (!scenarios) return {}
    return scenarios.reduce(
      (acc, scenario) => {
        acc[scenario.hazard_scenario] = scenario
        return acc
      },
      {} as Record<string, ScenariosByAsset>,
    )
  }, [scenarios])

  const address = useMemo(() => {
    if (selectedAssetInfo && typeof selectedAssetInfo === 'object') {
      if ('Address' in selectedAssetInfo) {
        return (selectedAssetInfo as Record<string, string | number>)['Address']
      }
      if ('Address(es)' in selectedAssetInfo) {
        return (selectedAssetInfo as Record<string, string | number>)['Address(es)']
      }
    }
    return undefined
  }, [selectedAssetInfo])

  const organizedReportHazards = useMemo(() => {
    if (!reportHazards) return []
    const sortedHazards = reportHazards
      .filter((group) => {
        // Filter out groups with either no hazards or no scenarios
        return group.hazards.some((hazard) => hazard.scenarios.length > 0)
      })
      .toSorted((a, b) =>
        a.group_name.localeCompare(b.group_name, undefined, {
          numeric: true,
        }),
      )
    return sortedHazards.map((group) => ({
      group_name: group.group_name,
      hazards: group.hazards.toSorted((a, b) => {
        // Helper to determine exposure order
        const getExposureOrder = (scenarioIds: string[]): number => {
          const isExposed = areScenariosExposed(scenarioIds, scenarios)
          if (isExposed === 'true') return 0
          if (isExposed === 'unknown') return 1
          return 2 // Not exposed
        }
        const exposureA = getExposureOrder(a.scenarios)
        const exposureB = getExposureOrder(b.scenarios)
        if (exposureA !== exposureB) return exposureA - exposureB
        return a.display_name.localeCompare(b.display_name, undefined, {
          numeric: true,
        })
      }),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportHazards])

  return (
    <Box css={tabParent(theme, propertyConfigLoading)}>
      <Box css={scrollContainer()}>
        <Box css={{ padding: '16px 0 36px 0' }}>
          <h2>Hazards</h2>
          {legalDescription && <b>{legalDescription}</b>}
          {address && <p>{address}</p>}
        </Box>
        {hazardsData && reportHazards ? (
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {organizedReportHazards.map(({ group_name, hazards }) => (
              <Accordion
                key={group_name}
                lazyContent={false}
                level="h3"
                variant="filled"
                defaultExpanded={true}
                header={
                  <Box css={{ display: 'flex', alignItems: 'center', padding: '8px 4px' }}>
                    {group_name}
                  </Box>
                }
                body={
                  <>
                    {scenarios &&
                      hazards.map((hazard) => (
                        <HazardAccordion
                          key={hazard.display_name}
                          isExposed={areScenariosExposed(hazard.scenarios, scenarios)}
                          hazard={hazard}
                          scenariosByUid={scenariosByUid}
                          technicalReports={technicalReports || null}
                          setPopupReport={setPopupReport}
                        />
                      ))}
                  </>
                }
              />
            ))}
          </Box>
        ) : (
          <Box css={noDetailsStyle}>No hazards information available for this property.</Box>
        )}
      </Box>
      <Box css={footerLegend}>
        {Object.values(isPropertyExposedLegend).map((legend) => (
          <Box key={legend.label}>
            <Box className="dot" sx={{ backgroundColor: legend.color }} />
            {legend.label}
          </Box>
        ))}
      </Box>
      {canExport && <SlideInFooter open={!selectedAssetInfoIsLoading && !knownRisksLoading} />}
      <TechnicalReportPopup
        report={popupReport}
        open={!!popupReport}
        property={
          selectedAssetInfo &&
          typeof selectedAssetInfo === 'object' &&
          !Array.isArray(selectedAssetInfo) &&
          'LegalDescription' in selectedAssetInfo
            ? String((selectedAssetInfo as { LegalDescription?: string | number }).LegalDescription)
            : ''
        }
        onClose={() => setPopupReport(null)}
      />
    </Box>
  )
}
